import { yupResolver } from '@hookform/resolvers/yup';
import {
  ApplicationSubmissionMethod,
  BenefitDocumentDto,
  BenefitDocumentType,
  BenefitStatus,
  CreateApplicationResultDto,
  CreateAutomaticApplicationDto,
  isDefined,
} from '@zorro/clients';
import { YesNo } from '@zorro/types';
import {
  AgentOrEmployee,
  DisplayPlanMarket,
  InitialPremiumProgress,
  PaymentStatusFormInput,
  UploadableBenefitDocument,
  isPersistedDocument,
  mapDisplayToPlanMarket,
  mapPlanMarketToDisplay,
  useAnalytics,
  usePaymentStatusForm,
  usePersistUploadedDocuments,
} from '@zorro/web/shared-ui';
import {
  createBenefitDocument,
  getEmployeeActions,
  useFlowId,
  useForm,
  useIdempotencyKey,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';

import {
  CarrierApplicationSubmitFormFields,
  getCarrierApplicationSubmitSchema,
} from './CarrierCompleteStepUtils';
import {
  useSpecialEnrollmentDefaults,
  useSpecialEnrollmentProof,
} from './useSpecialEnrollmentProof';

/**
 * Owned by the modal (not the step) so the form and the EnrollConnect result
 * survive step navigation — e.g. the review-details round-trip.
 */
export type CarrierApplicationFormState = {
  paymentStatusForm: UseFormReturn<PaymentStatusFormInput>;
  submissionForm: UseFormReturn<CarrierApplicationSubmitFormFields>;
  shouldHideCommunicationSection: boolean;
  requiredActions: string[];
  isEditMode: boolean;
  isEnrollConnectSubmitted: boolean;
  enrollConnectResult: CreateApplicationResultDto | null;
  submittedApplication: Pick<
    CreateApplicationResultDto,
    'applicationId' | 'submittedAt' | 'payment'
  > | null;
  setEnrollConnectResult: (result: CreateApplicationResultDto) => void;
  addSubmissionEvidence: (evidence: BenefitDocumentDto) => void;
  persistProofOfConsentDocuments: () => Promise<boolean>;
  persistSpecialEnrollmentDocuments: () => Promise<CreateAutomaticApplicationDto | null>;
  onSubmit: () => Promise<boolean>;
};

type UseCarrierApplicationForm = (props: {
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  benefitId?: string;
}) => CarrierApplicationFormState;

export const useCarrierApplicationForm: UseCarrierApplicationForm = ({
  employerId,
  employeeId,
  onboardingPeriodId,
  benefitId,
}) => {
  const idempotencyKey = useIdempotencyKey();
  const flowId = useFlowId();
  const { sendEvent } = useAnalytics();
  const [enrollConnectResult, setEnrollConnectResult] =
    useState<CreateApplicationResultDto | null>(null);
  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindOne',
    params: [employeeId, onboardingPeriodId],
  });
  const { data: benefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });
  const { data: submittedApplicationResult } = useMonolithQuery({
    method: 'applicationsControllerGetSubmittedApplication',
    params: benefitId && [benefitId],
  });
  const persistedApplication = submittedApplicationResult?.application;
  const { data: enrollmentCommunicationConditions } = useMonolithQuery({
    method:
      'enrollmentCommunicationsControllerGetEnrollmentCommunicationConditions',
    params: onboardingPeriod?.id && [onboardingPeriod.id, employerId],
    enabled: isDefined(onboardingPeriod?.enrollmentPeriodId),
  });

  const {
    paymentStatusForm,
    onSubmit: paymentStatusOnSubmit,
    handleSubmit: handleSubmitPaymentStatus,
  } = usePaymentStatusForm(benefitId, false, false);

  const {
    autoPaySetter,
    autoPayCompleted,
    initialPremiumHandler,
    initialPremiumCompleted,
  } = useWatch({ control: paymentStatusForm.control });

  const isEmployeeAutoPay =
    autoPaySetter === AgentOrEmployee.EMPLOYEE &&
    autoPayCompleted !== YesNo.YES;
  const isEmployeeInitialPayments =
    initialPremiumHandler === AgentOrEmployee.EMPLOYEE &&
    initialPremiumCompleted === InitialPremiumProgress.NOT_SET_UP;

  const conditionsWithPayment = enrollmentCommunicationConditions
    ? {
        ...enrollmentCommunicationConditions,
        isInitialPayments:
          isEmployeeInitialPayments && isDefined(initialPremiumCompleted),
        isAutoPay: isEmployeeAutoPay && isDefined(autoPayCompleted),
      }
    : undefined;

  const requiredActions = getEmployeeActions(conditionsWithPayment);

  const shouldHideCommunicationSection = conditionsWithPayment
    ? conditionsWithPayment.isMedicare || requiredActions.length === 0
    : false;

  const isMajorMedicalPlan =
    isDefined(benefitId) && benefits?.majorMedicalBenefit?.id === benefitId;
  const allBenefitDocuments = onboardingPeriod?.benefitDocuments ?? [];
  const previousApplicationEvidences = allBenefitDocuments.filter(
    ({ type }) => type === BenefitDocumentType.APPLICATION
  );
  const previousProofOfConsentDocuments = allBenefitDocuments.filter(
    ({ type }) => type === BenefitDocumentType.APPLICATION_CONSENT
  );

  const specialEnrollmentDefaults = useSpecialEnrollmentDefaults(
    employeeId,
    benefitId,
    allBenefitDocuments
  );

  const isEditMode =
    benefits?.majorMedicalBenefit?.status ===
    BenefitStatus.CARRIER_APPLICATION_SENT;

  const previousEvidence =
    isEditMode && previousApplicationEvidences.length === 0;

  const storedSubmissionMethod =
    benefits?.majorMedicalBenefit?.applicationSubmissionMethod;
  // EnrollConnect submissions are off-exchange only.
  const isEnrollConnectStored =
    storedSubmissionMethod === ApplicationSubmissionMethod.ENROLL_CONNECT;

  const submissionForm = useForm<CarrierApplicationSubmitFormFields>({
    mode: 'all',
    resolver: yupResolver(getCarrierApplicationSubmitSchema(isEditMode)),
    defaultValues: {
      applicationEvidences: previousApplicationEvidences,
      noApplicationEvidence: previousEvidence,
      brokerAttestationAccepted:
        isEditMode ||
        isDefined(persistedApplication) ||
        previousProofOfConsentDocuments.length > 0,
      proofOfConsentDocuments: previousProofOfConsentDocuments,
      ...specialEnrollmentDefaults,
      planMarket:
        isEnrollConnectStored || isDefined(persistedApplication)
          ? DisplayPlanMarket.OFF_EXCHANGE
          : (mapPlanMarketToDisplay(
              benefits?.majorMedicalBenefit?.plan?.planMarket
            ) ?? undefined),
      includeApplicationInstructionsEmail: true,
      submissionMethod:
        storedSubmissionMethod ??
        (isDefined(persistedApplication)
          ? ApplicationSubmissionMethod.ENROLL_CONNECT
          : undefined),
    },
  });

  const selectedSubmissionMethod = useWatch({
    control: submissionForm.control,
    name: 'submissionMethod',
  });
  const submittedApplication =
    enrollConnectResult?.status === CreateApplicationResultDto.status.CREATED
      ? enrollConnectResult
      : (persistedApplication ?? null);
  const isEnrollConnectSubmitted =
    selectedSubmissionMethod === ApplicationSubmissionMethod.ENROLL_CONNECT &&
    (isDefined(submittedApplication) || (isEditMode && isEnrollConnectStored));

  const { mutate: mutateBenefitStatus } = useMonolithMutation({
    method: 'benefitsControllerUpdateBenefitStatus',
    shouldReloadPage: false,
    shouldShowLoadingOverlay: false,
  });
  const { mutate: mutateApplicationSubmissionMethod } = useMonolithMutation({
    method: 'benefitsControllerUpdateApplicationSubmissionMethod',
    shouldReloadPage: false,
    shouldShowLoadingOverlay: false,
  });
  const { mutate: sendCarrierApplicationEmail } = useMonolithMutation({
    method: 'enrollmentCommunicationsControllerSendCarrierApplicationEmail',
    shouldReloadPage: false,
    shouldShowLoadingOverlay: false,
  });
  const { mutate: mutateMajorMedical } = useMonolithMutation({
    method: 'majorMedicalControllerPatchMajorMedicalPlan',
    shouldReloadPage: false,
    shouldShowLoadingOverlay: false,
    shouldInvalidateQueries: false,
  });
  const { mutate: mutateBenefitDocument } = useMonolithMutation({
    method: 'benefitDocumentsControllerCreateBenefitDocument',
    shouldReloadPage: false,
    shouldShowLoadingOverlay: false,
  });
  const { mutate: mutateBenefitDocumentRemove } = useMonolithMutation({
    method: 'benefitDocumentsControllerRemoveBenefitDocument',
    shouldReloadPage: false,
    shouldShowLoadingOverlay: false,
    shouldInvalidateQueries: false,
  });
  const persistDocuments = usePersistUploadedDocuments({
    employerId,
    employeeId,
    onboardingPeriodId,
    documentType: BenefitDocumentType.APPLICATION_CONSENT,
    documentLabel: 'proof of consent',
  });
  const persistSpecialEnrollmentDocuments = useSpecialEnrollmentProof({
    employerId,
    employeeId,
    onboardingPeriodId,
    submissionForm,
  });

  const addSubmissionEvidence = (evidence: BenefitDocumentDto) => {
    const currentEvidences = submissionForm.getValues('applicationEvidences');
    submissionForm.setValue(
      'applicationEvidences',
      [...(Array.isArray(currentEvidences) ? currentEvidences : []), evidence],
      { shouldValidate: true, shouldDirty: true }
    );
  };

  // Runs at the submit action AND at the final confirm (edit mode's only save
  // point).
  const persistProofOfConsentDocuments = async (): Promise<boolean> => {
    const documents = submissionForm.getValues('proofOfConsentDocuments');
    if (!Array.isArray(documents)) return true;

    const result = await persistDocuments(documents);
    submissionForm.setValue('proofOfConsentDocuments', result.documents, {
      shouldDirty: true,
    });
    return result.isSuccess;
  };

  const uploadEvidence = async (
    applicationEvidence: UploadableBenefitDocument[] | undefined
  ) => {
    if (!applicationEvidence) return;

    const filesToCreate = applicationEvidence.filter(
      (item): item is File => item instanceof File
    );

    const newDocuments = await Promise.all(
      filesToCreate.map((file) =>
        createBenefitDocument(
          employeeId,
          employerId,
          file,
          onboardingPeriodId,
          true,
          BenefitDocumentType.APPLICATION
        )
      )
    );

    const keptIds = applicationEvidence
      .filter(isPersistedDocument)
      .map((doc) => doc.id);
    // The submit response doesn't refetch the period's documents, so its evidence is added here
    // for a removal in this session to reach the server.
    const submissionEvidence = enrollConnectResult?.submissionEvidence;
    const previousIds = new Set(
      [
        ...allBenefitDocuments.filter(
          ({ type }) => type === BenefitDocumentType.APPLICATION
        ),
        ...(submissionEvidence ? [submissionEvidence] : []),
      ].map(({ id }) => id)
    );
    const idsToRemove = [...previousIds].filter((id) => !keptIds.includes(id));

    await Promise.all(newDocuments.map((doc) => mutateBenefitDocument([doc])));
    await Promise.all(
      idsToRemove.map((id) => mutateBenefitDocumentRemove([id]))
    );
  };
  const onSubmit = async () => {
    if (!benefitId) return false;

    if (!(await paymentStatusForm.trigger())) return false;

    if (!(await persistProofOfConsentDocuments())) return false;

    const {
      applicationEvidences,
      planMarket: planMarketDisplay,
      includeApplicationInstructionsEmail,
      submissionMethod,
    } = submissionForm.getValues();
    const planMarket = mapDisplayToPlanMarket(
      submissionMethod === ApplicationSubmissionMethod.ENROLL_CONNECT
        ? DisplayPlanMarket.OFF_EXCHANGE
        : planMarketDisplay
    );

    const shouldSendEmail =
      !shouldHideCommunicationSection &&
      includeApplicationInstructionsEmail &&
      isMajorMedicalPlan;

    void sendEvent('click_confirm_application_sent', {
      onboarding_period_id: onboardingPeriodId,
    });

    const { isMedicare } = enrollmentCommunicationConditions || {};

    try {
      if (submissionMethod) {
        await mutateApplicationSubmissionMethod(
          [benefitId, { applicationSubmissionMethod: submissionMethod }],
          { flowId }
        );
      }

      if (isMajorMedicalPlan) {
        await mutateMajorMedical([benefitId, { planMarket }], { flowId });
      }

      await handleSubmitPaymentStatus(paymentStatusOnSubmit)();

      await mutateBenefitStatus(
        [
          benefitId,
          idempotencyKey.get(),
          { status: BenefitStatus.CARRIER_APPLICATION_SENT },
        ],
        { flowId }
      );

      if (shouldSendEmail) {
        await sendCarrierApplicationEmail([
          onboardingPeriodId,
          {
            ...(!isMedicare && {
              isAutoPay: isEmployeeAutoPay,
              isInitialPayments: isEmployeeInitialPayments,
            }),
          },
        ]);
      }

      await uploadEvidence(applicationEvidences);

      return true;
    } catch {
      return false;
    }
  };

  return {
    paymentStatusForm,
    submissionForm,
    shouldHideCommunicationSection,
    requiredActions,
    isEditMode,
    isEnrollConnectSubmitted,
    enrollConnectResult,
    submittedApplication,
    setEnrollConnectResult,
    addSubmissionEvidence,
    persistProofOfConsentDocuments,
    persistSpecialEnrollmentDocuments,
    onSubmit,
  };
};
